/**
 * useLiveInterviewCall — Part 0 of Live Interview Phase C: wires real
 * RTCPeerConnections on top of the session-isolated WebSocket signaling
 * relay Phase A built (backend/api/v2/endpoints/live_interview_signaling.py)
 * but never actually negotiated over. Shared by both room pages
 * (LiveInterviewRoomPage.tsx — candidate, RecruiterRoomPage.tsx —
 * recruiter) since the negotiation logic is identical on both sides; only
 * how each page acquires its own local media and session id differs.
 *
 * SIMPLIFYING ASSUMPTIONS (explicitly flagged, not oversights):
 *  - Full mesh, not an SFU: every participant opens one RTCPeerConnection
 *    per OTHER participant. Fine for the small (candidate + a few
 *    recruiter seats) rooms this feature targets; would not scale to a
 *    large panel.
 *  - Deterministic initiator selection instead of full "perfect
 *    negotiation": whichever peer has the lexicographically LOWER
 *    server-assigned peer_id always sends the offer for a given pair. This
 *    avoids a simultaneous-offer glare without implementing rollback/
 *    polite-peer renegotiation — sufficient because this hook never
 *    renegotiates a connection after its initial offer/answer (no
 *    mid-call track add/remove).
 *  - The hook only starts once `enabled` (typically: local media acquired
 *    + a valid session id known) — it does not add local media lazily to
 *    an already-open connection, so there is no renegotiation path to
 *    handle either.
 *  - ICE candidates that arrive before the corresponding remote description
 *    is set are queued and flushed right after, rather than dropped/
 *    retried — the one piece of "real" robustness this pass includes,
 *    since it is a near-certain race (candidates commonly arrive over the
 *    signaling channel before the offer/answer round trip finishes).
 *
 * ---------------------------------------------------------------------------
 * RECONNECTION
 * ---------------------------------------------------------------------------
 * The signaling socket used to be one-shot: `onclose` flipped `connected`
 * false and nothing ever re-opened it, so any network blip left the
 * participant sitting in a room that looked live but was deaf to every
 * subsequent message. It now retries on a bounded backoff
 * (RECONNECT_DELAYS_MS) until `reconnectTimeoutMs` has elapsed since the
 * FIRST drop — not per attempt — and only then gives up and reports a hard
 * 'disconnected'. That budget is server-configured
 * (Settings.LIVE_INTERVIEW_RECONNECT_TIMEOUT_SECONDS, delivered on the
 * per-session config read each room already does) rather than hardcoded.
 *
 * Re-identifying on reconnect needs no explicit "join" message: this client's
 * identity travels as query params on the socket URL (role + bearer token),
 * so simply dialling the same URL again re-runs the server's whole join
 * sequence, including the eviction of this participant's own now-dead
 * previous connection.
 *
 * THE PEER-ID CHANGE — the part that is NOT like VMP's SignalR port. Each
 * connection gets a fresh server-assigned `peer_id`, and evicting our stale
 * connection makes the server broadcast `peer-left` for our OLD id, which
 * makes every remote peer close its RTCPeerConnection to us. Our local
 * halves of those connections are therefore dead too — keeping them (as VMP
 * deliberately does, leaving WebRTC's own ICE recovery to handle blips)
 * would leave `ensurePeerConnection` handing back corpses that can never
 * renegotiate. So on a reconnect that changes our peer id we tear the mesh
 * down and rebuild it from the fresh `welcome` roster. The one thing
 * `resetPeerConnections` deliberately does NOT clear is
 * `outgoingScreenTrackRef`/`outgoingScreenStreamRef`: a screen share in
 * progress must survive the blip, and the rebuilt connections
 * (`ensurePeerConnection`) read them to add the screen track alongside the
 * camera again from the start, rather than losing it on reconnect.
 *
 * ---------------------------------------------------------------------------
 * SCREEN SHARE: A SECOND TRACK, NOT A REPLACEMENT
 * ---------------------------------------------------------------------------
 * Confirmed against VMP's own Live Interview implementation (same product
 * family, working in production): screen sharing adds a SECOND video
 * track/transceiver to every peer connection (via addTrack, on its own
 * dedicated MediaStream) rather than swapping the camera's outgoing track —
 * so a presenter's camera keeps flowing to everyone else alongside their
 * screen. That makes this the one place this hook DOES renegotiate mid-call,
 * which the rest of the file's design (deterministic single-shot initial
 * offer, no track add/remove) deliberately avoids elsewhere.
 *
 * Renegotiation is ALWAYS initiated by the sharer, toward every peer, via
 * startScreenShare/stopScreenShare below — never automatically from
 * `onnegotiationneeded` (VMP attaches that unconditionally and relies on a
 * bare `signalingState !== 'stable'` guard, which does not fully solve
 * simultaneous-offer glare; this hook already has an explicit, deterministic
 * initial-offer scheme via `initiateOfferTo`, so wiring `onnegotiationneeded`
 * in ADDITION to that would double-negotiate the very first connect). Because
 * only the sharer ever calls createOffer for a renegotiation, and the app
 * layer (useLiveInterviewMeeting) already enforces "at most one presenter at
 * a time," there is no cross-peer renegotiation glare to resolve here — the
 * only real race is the SAME peer's own share/unshare happening in quick
 * succession, which `renegotiateWith`'s per-peer promise queue serializes
 * (each renegotiation waits for the PREVIOUS one's answer, tracked via
 * `negotiationResolversRef`, before sending its own offer).
 *
 * A peer that joins WHILE a share is already in progress gets both tracks
 * from its very FIRST offer (ensurePeerConnection adds the screen track
 * alongside the camera track up front when `outgoingScreenTrackRef` is
 * already set) — no separate renegotiation needed for that case.
 *
 * DISTINGUISHING CAMERA VS. SCREEN ON THE RECEIVING END: VMP resolves this
 * purely by MediaStream arrival order/id ("first stream seen = camera, later
 * distinct one = screen"), which races against a late joiner receiving both
 * tracks in the same offer. This hook keeps that same heuristic as a
 * fallback but also has `useLiveInterviewMeeting` tell it explicitly which
 * stream id is the screen, via `setPeerScreenStreamId` — sourced from the
 * `media-state` app message's `screen_stream_id` field (set the moment the
 * sharer announces `sharing: true`). Every stream a peer sends is buffered
 * by its MediaStream id (`streamsByPeerRef`); `resolvePeerTile` re-derives
 * camera vs. screen from both signals every time either changes, so the
 * result is correct regardless of which arrived first.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { API_BASE_URL } from '@/services/api'

export interface RemoteTile {
  peerId: string
  /** This peer's camera. */
  stream: MediaStream
  /** This peer's shared screen, while they're presenting; null otherwise. */
  screenStream: MediaStream | null
}

/**
 * Whether the SIGNALING connection itself is up, actively retrying, or has
 * given up — deliberately independent of `networkQuality`, which only ever
 * describes the WebRTC peer connections' stats while signaling IS up.
 *
 * 'disconnected' vs. 'failed' are deliberately distinct terminal states:
 * 'disconnected' is a clean, EXPECTED close (the server evicting this
 * connection's stale duplicate tab, see the code===1000 branch below) —
 * nothing further should happen. 'failed' means the reconnect budget was
 * genuinely exhausted after a real network/server outage — Teams-style,
 * this is the signal a room page uses to stop waiting and force the
 * participant out (see RecruiterRoomPage/LiveInterviewRoomPage's own
 * "connection lost" handling) rather than leaving them stuck on a page
 * that looks alive but never recovers.
 */
export type LiveInterviewConnectionState = 'connected' | 'reconnecting' | 'disconnected' | 'failed'

/** Worst-link WebRTC media quality across every peer connection. */
export type LiveInterviewNetworkQuality = 'good' | 'fair' | 'poor' | 'unknown'

/**
 * Backoff between reconnect attempts, indexed by attempt number and clamped
 * to the last entry. The leading 0 makes the first retry immediate, which
 * covers the overwhelmingly common case of a momentary blip.
 */
const RECONNECT_DELAYS_MS = [0, 1000, 2000, 5000, 10000]

/** Used only if the server never told us its own value. */
const DEFAULT_RECONNECT_TIMEOUT_MS = 60_000

/** How often peer-connection stats are sampled for the quality indicator. */
const NETWORK_SAMPLE_INTERVAL_MS = 5_000

export interface LiveInterviewIceServer {
  urls: string
  username?: string
  credential?: string
}

interface UseLiveInterviewCallOptions {
  sessionId: string | null | undefined
  localStream: MediaStream | null
  iceServers: LiveInterviewIceServer[] | null
  enabled: boolean
  // Phase D, Part 0: this socket has no native way to authenticate a
  // recruiter's connection (browsers' WebSocket API can't set an
  // Authorization header) — the server instead expects `role` (and, for a
  // recruiter, its own bearer `token`) as query params on connect, mirroring
  // VMP's JoinSession(sessionId, role) shape. Defaults to "candidate" server
  // -side when omitted, matching every pre-Phase-D caller of this hook.
  role?: 'recruiter' | 'candidate'
  authToken?: string | null
  // How long to keep retrying the signaling socket after it drops, measured
  // from the FIRST drop rather than per attempt. Sourced from the backend
  // (LIVE_INTERVIEW_RECONNECT_TIMEOUT_SECONDS, carried on the per-session
  // config read each room already performs) so ops can tune it without a
  // frontend redeploy; falls back to DEFAULT_RECONNECT_TIMEOUT_MS only if the
  // caller has nothing to pass yet.
  reconnectTimeoutMs?: number
  // Any signaling message whose "type" isn't part of the core WebRTC
  // negotiation protocol (offer/answer/ice-candidate/welcome/peer-joined/
  // peer-left) is handed to this callback instead — this is the channel
  // Part 4's showQuestion/hideQuestion signal (and Phase D's `chat` messages)
  // ride on top of, without a second WebSocket connection.
  onAppMessage?: (message: Record<string, any>) => void
}

function buildSignalingUrl(sessionId: string, role?: string, authToken?: string | null): string {
  const wsBase = API_BASE_URL.replace(/^http/, 'ws')
  const params = new URLSearchParams()
  if (role) params.set('role', role)
  if (authToken) params.set('token', authToken)
  const query = params.toString()
  return `${wsBase}/api/v2/live-interview/signaling/${sessionId}${query ? `?${query}` : ''}`
}

export function useLiveInterviewCall({
  sessionId, localStream, iceServers, enabled, role, authToken, reconnectTimeoutMs, onAppMessage,
}: UseLiveInterviewCallOptions) {
  const [remoteTiles, setRemoteTiles] = useState<RemoteTile[]>([])
  const [connected, setConnected] = useState(false)
  // Starts optimistic rather than 'reconnecting': before the first socket has
  // even been dialled there is nothing to reconnect TO, and the rooms already
  // render their own "Connecting…" text off `connected`.
  const [connectionState, setConnectionState] = useState<LiveInterviewConnectionState>('connected')
  const [networkQuality, setNetworkQuality] = useState<LiveInterviewNetworkQuality>('unknown')
  const [ownPeerId, setOwnPeerId] = useState<string | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const ownPeerIdRef = useRef<string | null>(null)
  const pcsRef = useRef<Map<string, RTCPeerConnection>>(new Map())
  const pendingCandidatesRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map())
  // Screen share is a SECOND track/transceiver added alongside the camera's
  // (see file header) — screenSendersRef keeps each peer connection's screen
  // sender so stopScreenShare can remove exactly that transceiver; outgoing
  // ScreenTrack/StreamRef remember what is currently being shared so a peer
  // that joins DURING a share is negotiated with both tracks from the start.
  const screenSendersRef = useRef<Map<string, RTCRtpSender>>(new Map())
  const outgoingScreenTrackRef = useRef<MediaStreamTrack | null>(null)
  const outgoingScreenStreamRef = useRef<MediaStream | null>(null)
  // Every stream a peer has ever sent, by MediaStream id — resolved into
  // "camera" vs. "screen" by resolvePeerTile once setPeerScreenStreamId says
  // which id is the screen (see file header).
  const streamsByPeerRef = useRef<Map<string, Map<string, MediaStream>>>(new Map())
  const screenStreamIdByPeerRef = useRef<Map<string, string>>(new Map())
  // Per-peer renegotiation queue: each entry resolves once that peer's ANSWER
  // to the renegotiation offer has been applied, so a quick share-then-unshare
  // on the same peer serializes instead of racing two offers against one
  // signaling state.
  const renegotiationQueueRef = useRef<Map<string, Promise<void>>>(new Map())
  const negotiationResolversRef = useRef<Map<string, () => void>>(new Map())
  const onAppMessageRef = useRef(onAppMessage)
  onAppMessageRef.current = onAppMessage
  // Read through a ref, never a dep: the value arrives from an async config
  // read, and letting it into the connection effect's dep list would tear a
  // perfectly healthy socket down the moment it landed.
  const reconnectTimeoutMsRef = useRef(reconnectTimeoutMs)
  reconnectTimeoutMsRef.current = reconnectTimeoutMs
  // Previous getStats() totals per peer. packetsLost/packetsReceived are
  // cumulative for the life of a connection, so a raw ratio would describe
  // the whole call and barely move after a bad patch; the indicator is meant
  // to show how the link is doing NOW, hence the per-interval delta.
  const statsHistoryRef = useRef<Map<string, { lost: number; received: number }>>(new Map())

  const send = useCallback((message: Record<string, any>) => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(message))
  }, [])

  // Recomputes one peer's tile from whatever streams/screen-id are currently
  // known for it — the single point both `ontrack` and setPeerScreenStreamId
  // funnel through, so the result is correct no matter which arrives first
  // (see file header).
  const resolvePeerTile = useCallback((peerId: string) => {
    const streams = streamsByPeerRef.current.get(peerId)
    if (!streams || streams.size === 0) return
    const screenId = screenStreamIdByPeerRef.current.get(peerId)
    let cameraStream: MediaStream | undefined
    let screenStream: MediaStream | undefined
    streams.forEach((s, id) => {
      if (screenId && id === screenId) screenStream = s
      else if (!cameraStream) cameraStream = s
    })
    // Two streams known but we haven't been told which id is the screen yet
    // (the media-state announcement racing the tracks themselves) — keep the
    // first as camera provisionally, same as VMP's plain arrival-order
    // heuristic; resolvePeerTile re-runs and corrects this the moment
    // setPeerScreenStreamId lands.
    if (!screenStream && !screenId && streams.size >= 2) {
      const iter = streams.values()
      cameraStream = iter.next().value
      screenStream = iter.next().value
    }
    if (!cameraStream) return
    const resolvedCamera = cameraStream
    const resolvedScreen = screenStream || null
    setRemoteTiles((prev) => {
      const exists = prev.some((t) => t.peerId === peerId)
      const next: RemoteTile = { peerId, stream: resolvedCamera, screenStream: resolvedScreen }
      return exists ? prev.map((t) => (t.peerId === peerId ? next : t)) : [...prev, next]
    })
  }, [])

  const closePeer = useCallback((peerId: string) => {
    pcsRef.current.get(peerId)?.close()
    pcsRef.current.delete(peerId)
    pendingCandidatesRef.current.delete(peerId)
    screenSendersRef.current.delete(peerId)
    streamsByPeerRef.current.delete(peerId)
    screenStreamIdByPeerRef.current.delete(peerId)
    renegotiationQueueRef.current.delete(peerId)
    negotiationResolversRef.current.delete(peerId)
    statsHistoryRef.current.delete(peerId)
    setRemoteTiles((prev) => prev.filter((t) => t.peerId !== peerId))
  }, [])

  /**
   * Drops the entire peer mesh, keeping the screen-share track. Used when a
   * reconnect hands us a NEW peer id: every remote peer has already closed
   * its side (they saw our old id leave), so these connections can never
   * recover and must be rebuilt from the fresh roster. See the file header.
   */
  const resetPeerConnections = useCallback(() => {
    pcsRef.current.forEach((pc) => pc.close())
    pcsRef.current.clear()
    pendingCandidatesRef.current.clear()
    screenSendersRef.current.clear()
    streamsByPeerRef.current.clear()
    screenStreamIdByPeerRef.current.clear()
    renegotiationQueueRef.current.clear()
    negotiationResolversRef.current.clear()
    statsHistoryRef.current.clear()
    setRemoteTiles([])
  }, [])

  /**
   * Sends a fresh offer to ONE peer for whatever tracks are on the
   * connection right now, and waits for that peer's answer to actually land
   * (via negotiationResolversRef, resolved from the 'answer' handler below)
   * before resolving — so a second renegotiation with the same peer, queued
   * right after, never starts while this one is still in flight. See file
   * header.
   */
  const renegotiateWith = useCallback((peerId: string, pc: RTCPeerConnection): Promise<void> => {
    const prior = renegotiationQueueRef.current.get(peerId) || Promise.resolve()
    const next = prior.then(
      () =>
        new Promise<void>((resolve) => {
          negotiationResolversRef.current.set(peerId, resolve)
          void (async () => {
            try {
              const offer = await pc.createOffer()
              await pc.setLocalDescription(offer)
              send({ type: 'offer', to: peerId, sdp: pc.localDescription })
            } catch {
              // The peer's connection is on its way out anyway — nothing to
              // retry into.
              negotiationResolversRef.current.delete(peerId)
              resolve()
            }
          })()
        })
    )
    renegotiationQueueRef.current.set(peerId, next)
    return next
  }, [send])

  /**
   * Starts sending this screen-capture track/stream to EVERY connected peer,
   * as a SECOND track alongside the camera (see file header) — never call
   * this with an already-active share; stop it first.
   */
  const startScreenShare = useCallback((track: MediaStreamTrack, stream: MediaStream) => {
    outgoingScreenTrackRef.current = track
    outgoingScreenStreamRef.current = stream
    pcsRef.current.forEach((pc, peerId) => {
      if (screenSendersRef.current.has(peerId)) return
      const sender = pc.addTrack(track, stream)
      screenSendersRef.current.set(peerId, sender)
      void renegotiateWith(peerId, pc)
    })
  }, [renegotiateWith])

  /** Stops sending the screen track to every peer and tears down its transceiver. */
  const stopScreenShare = useCallback(() => {
    outgoingScreenTrackRef.current = null
    outgoingScreenStreamRef.current = null
    pcsRef.current.forEach((pc, peerId) => {
      const sender = screenSendersRef.current.get(peerId)
      if (!sender) return
      pc.removeTrack(sender)
      screenSendersRef.current.delete(peerId)
      void renegotiateWith(peerId, pc)
    })
  }, [renegotiateWith])

  /**
   * Tells this hook which of a peer's known stream ids is their SCREEN
   * (sourced from the `media-state` app message's `screen_stream_id` —
   * useLiveInterviewMeeting calls this), or clears it when they stop
   * sharing. Recomputes that peer's tile immediately either way.
   */
  const setPeerScreenStreamId = useCallback((peerId: string, streamId: string | null) => {
    if (streamId) screenStreamIdByPeerRef.current.set(peerId, streamId)
    else screenStreamIdByPeerRef.current.delete(peerId)
    resolvePeerTile(peerId)
  }, [resolvePeerTile])

  const flushPendingCandidates = useCallback(async (peerId: string, pc: RTCPeerConnection) => {
    const queued = pendingCandidatesRef.current.get(peerId)
    if (!queued || queued.length === 0) return
    pendingCandidatesRef.current.delete(peerId)
    for (const candidate of queued) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate))
      } catch {
        // Best-effort — a stale/duplicate candidate failing to add isn't
        // fatal to the overall connection.
      }
    }
  }, [])

  const ensurePeerConnection = useCallback(
    (peerId: string): RTCPeerConnection => {
      const existing = pcsRef.current.get(peerId)
      if (existing) return existing

      const pc = new RTCPeerConnection({ iceServers: iceServers || undefined })
      if (localStream) localStream.getTracks().forEach((track) => pc.addTrack(track, localStream))
      // A peer that joins DURING an active share must be negotiated with the
      // screen track from its very first offer, alongside the camera above —
      // added as its own transceiver (a second stream), never in place of it.
      if (outgoingScreenTrackRef.current && outgoingScreenStreamRef.current) {
        const sender = pc.addTrack(outgoingScreenTrackRef.current, outgoingScreenStreamRef.current)
        screenSendersRef.current.set(peerId, sender)
      }
      pc.onicecandidate = (event) => {
        if (event.candidate) send({ type: 'ice-candidate', to: peerId, candidate: event.candidate.toJSON() })
      }
      pc.ontrack = (event) => {
        const stream = event.streams[0]
        if (!stream) return
        let byId = streamsByPeerRef.current.get(peerId)
        if (!byId) {
          byId = new Map()
          streamsByPeerRef.current.set(peerId, byId)
        }
        byId.set(stream.id, stream)
        const streamMap = byId
        // A track ending (screen share stopped, or the remote peer's camera
        // itself going away) is the one reliable signal that THIS stream is
        // gone — forgetting it here (rather than only via the app-level
        // media-state message) keeps this correct even if that message is
        // ever lost.
        event.track.onended = () => {
          streamMap.delete(stream.id)
          if (screenStreamIdByPeerRef.current.get(peerId) === stream.id) {
            screenStreamIdByPeerRef.current.delete(peerId)
          }
          resolvePeerTile(peerId)
        }
        resolvePeerTile(peerId)
      }
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'closed') closePeer(peerId)
      }
      pcsRef.current.set(peerId, pc)
      return pc
    },
    [iceServers, localStream, send, closePeer, resolvePeerTile]
  )

  const initiateOfferTo = useCallback(
    async (peerId: string) => {
      const pc = ensurePeerConnection(peerId)
      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)
      send({ type: 'offer', to: peerId, sdp: pc.localDescription })
    },
    [ensurePeerConnection, send]
  )

  useEffect(() => {
    if (!enabled || !sessionId || !localStream || !iceServers) return
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    // Attempt counter (indexes RECONNECT_DELAYS_MS) and the timestamp of the
    // first drop in the CURRENT outage. Both reset on a successful open, so a
    // call that blips repeatedly over an hour gets the full budget each time
    // rather than accumulating towards a single lifetime limit.
    let attempt = 0
    let firstDropAt: number | null = null

    const handleMessage = async (event: MessageEvent) => {
      if (cancelled) return
      let message: any
      try {
        message = JSON.parse(event.data)
      } catch {
        return
      }
      const type = message?.type

      if (type === 'welcome') {
        // A `welcome` when we already had a peer id means this is a
        // reconnect, and the id we are being given is a NEW one — so the
        // mesh built against our previous id is unrecoverable on both ends.
        // See the file header's RECONNECTION section.
        const previousPeerId = ownPeerIdRef.current
        if (previousPeerId && previousPeerId !== message.peer_id) resetPeerConnections()
        ownPeerIdRef.current = message.peer_id
        setOwnPeerId(message.peer_id)
        const existingPeerIds: string[] = message.peers || []
        // Deterministic initiator per pair — see file header. Only the
        // side with the lower id offers; the other waits for it.
        existingPeerIds.forEach((peerId) => {
          if (ownPeerIdRef.current! < peerId) void initiateOfferTo(peerId)
        })
        onAppMessageRef.current?.(message)
      } else if (type === 'peer-joined') {
        const peerId = message.peer_id
        if (peerId && ownPeerIdRef.current && ownPeerIdRef.current < peerId) {
          void initiateOfferTo(peerId)
        }
        // Also surfaced to the app layer: a peer that just joined knows
        // nothing about who is muted / camera-off / presenting (those are
        // broadcast-once app messages, not media-level state), so the
        // meeting UI re-announces its own state when it sees this.
        onAppMessageRef.current?.(message)
      } else if (type === 'peer-left') {
        closePeer(message.peer_id)
        onAppMessageRef.current?.(message)
      } else if (type === 'offer') {
        const peerId = message.from
        if (!peerId) return
        const pc = ensurePeerConnection(peerId)
        await pc.setRemoteDescription(new RTCSessionDescription(message.sdp))
        await flushPendingCandidates(peerId, pc)
        const answer = await pc.createAnswer()
        await pc.setLocalDescription(answer)
        send({ type: 'answer', to: peerId, sdp: pc.localDescription })
      } else if (type === 'answer') {
        const peerId = message.from
        const pc = peerId ? pcsRef.current.get(peerId) : undefined
        if (pc) {
          await pc.setRemoteDescription(new RTCSessionDescription(message.sdp))
          await flushPendingCandidates(peerId, pc)
          // Unblocks renegotiateWith's per-peer queue for a screen-share
          // start/stop — a no-op for the INITIAL connection's answer, since
          // that negotiation never went through the queue in the first place.
          const resolveRenegotiation = peerId ? negotiationResolversRef.current.get(peerId) : undefined
          if (peerId && resolveRenegotiation) {
            negotiationResolversRef.current.delete(peerId)
            resolveRenegotiation()
          }
        }
      } else if (type === 'ice-candidate') {
        const peerId = message.from
        if (!peerId || !message.candidate) return
        const pc = pcsRef.current.get(peerId)
        if (pc && pc.remoteDescription) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(message.candidate))
          } catch {
            /* best-effort */
          }
        } else {
          const queue = pendingCandidatesRef.current.get(peerId) || []
          queue.push(message.candidate)
          pendingCandidatesRef.current.set(peerId, queue)
        }
      } else {
        onAppMessageRef.current?.(message)
      }
    }

    // Dials the signaling URL — identical every time, which is exactly how a
    // reconnect re-identifies itself (role + bearer token are query params on
    // it), so there is no separate "join" message to re-send.
    const openSocket = () => {
      if (cancelled) return
      const ws = new WebSocket(buildSignalingUrl(sessionId, role, authToken))
      wsRef.current = ws

      ws.onopen = () => {
        if (cancelled) return
        attempt = 0
        firstDropAt = null
        setConnected(true)
        setConnectionState('connected')
      }

      ws.onclose = (event) => {
        if (cancelled) return
        if (wsRef.current === ws) wsRef.current = null
        setConnected(false)

        // A DELIBERATE server-side close is never retried. The server closes
        // cleanly (code 1000) when it evicts this participant's stale
        // connection — which is exactly what it does to the older of two tabs
        // open on the same session. Reconnecting out of that would have the
        // evicted tab immediately evict the live one, which would reconnect
        // and evict it back, forever: two tabs knocking each other out of the
        // meeting for as long as both stay open. A genuine network drop never
        // reports 1000 (it surfaces as 1006/abnormal), so this costs nothing
        // in the case reconnection actually exists for.
        //
        // Accepted trade-off: a graceful BACKEND shutdown also closes cleanly,
        // and those rooms will not self-heal on restart. A hard restart (the
        // common case) drops connections abnormally and does reconnect.
        if (event.code === 1000) {
          setConnectionState('disconnected')
          return
        }

        if (firstDropAt === null) firstDropAt = Date.now()

        const budgetMs = Math.max(0, reconnectTimeoutMsRef.current ?? DEFAULT_RECONNECT_TIMEOUT_MS)
        // Same rule as VMP's retry policy: keep going while we are still
        // INSIDE the budget, give up the moment we are not. Nothing schedules
        // another attempt from here, so 'failed' is terminal for this mount —
        // the room reacts to it by forcing a leave (see room pages), rather
        // than leaving the participant stuck on a page that looks alive but
        // never recovers.
        if (Date.now() - firstDropAt >= budgetMs) {
          setConnectionState('failed')
          return
        }

        setConnectionState('reconnecting')
        const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)]
        attempt += 1
        retryTimer = setTimeout(openSocket, delay)
      }

      ws.onmessage = handleMessage
    }

    openSocket()

    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
      wsRef.current?.close()
      wsRef.current = null
      pcsRef.current.forEach((pc) => pc.close())
      pcsRef.current.clear()
      pendingCandidatesRef.current.clear()
      screenSendersRef.current.clear()
      streamsByPeerRef.current.clear()
      screenStreamIdByPeerRef.current.clear()
      renegotiationQueueRef.current.clear()
      negotiationResolversRef.current.clear()
      statsHistoryRef.current.clear()
      outgoingScreenTrackRef.current = null
      outgoingScreenStreamRef.current = null
      ownPeerIdRef.current = null
      setOwnPeerId(null)
      setRemoteTiles([])
      setConnected(false)
      setConnectionState('connected')
      setNetworkQuality('unknown')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sessionId, localStream, iceServers])

  // --- Network quality sampling ----------------------------------------
  // Polls every peer connection's own WebRTC stats and reports the WORST
  // link, because a call is only as good as its weakest leg — one peer
  // dropping packets is a degraded call even if the others are pristine.
  // Purely observational: nothing here touches the connections themselves.
  useEffect(() => {
    if (!enabled) {
      setNetworkQuality('unknown')
      return
    }
    let cancelled = false

    const sample = async () => {
      const peers = Array.from(pcsRef.current.entries())
      if (peers.length === 0) {
        if (!cancelled) setNetworkQuality('unknown')
        return
      }
      const tiers = await Promise.all(peers.map(([peerId, pc]) => measurePeerQuality(peerId, pc, statsHistoryRef.current)))
      if (cancelled) return
      const known = tiers.filter((t): t is Exclude<LiveInterviewNetworkQuality, 'unknown'> => t !== 'unknown')
      if (known.length === 0) {
        setNetworkQuality('unknown')
        return
      }
      setNetworkQuality(known.includes('poor') ? 'poor' : known.includes('fair') ? 'fair' : 'good')
    }

    void sample()
    const interval = setInterval(() => { void sample() }, NETWORK_SAMPLE_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [enabled])

  return {
    remoteTiles,
    connected,
    connectionState,
    networkQuality,
    ownPeerId,
    sendAppMessage: send,
    startScreenShare,
    stopScreenShare,
    setPeerScreenStreamId,
  }
}

/**
 * One peer connection's quality tier, from its own getStats(). Loss is
 * measured as a DELTA against the previous sample (the raw counters are
 * cumulative for the connection's whole life, so a raw ratio would describe
 * the entire call and barely respond to a bad patch); round-trip time is the
 * worst nominated candidate pair's.
 *
 * Returns 'unknown' rather than guessing when the connection has not yet
 * reported anything useful — a call that just started has no packets to
 * judge, and showing "poor" there would be a lie.
 */
async function measurePeerQuality(
  peerId: string,
  pc: RTCPeerConnection,
  history: Map<string, { lost: number; received: number }>
): Promise<LiveInterviewNetworkQuality> {
  if (pc.connectionState === 'failed' || pc.connectionState === 'closed') return 'poor'

  const rtts: number[] = []
  let lost = 0
  let received = 0
  try {
    const stats = await pc.getStats()
    stats.forEach((report: any) => {
      if (report?.type === 'candidate-pair' && report.nominated && typeof report.currentRoundTripTime === 'number') {
        rtts.push(report.currentRoundTripTime)
      } else if (report?.type === 'inbound-rtp' && !report.isRemote) {
        if (typeof report.packetsLost === 'number') lost += report.packetsLost
        if (typeof report.packetsReceived === 'number') received += report.packetsReceived
      }
    })
  } catch {
    // getStats can reject on a connection that is closing out from under us.
    return 'unknown'
  }

  const previous = history.get(peerId)
  history.set(peerId, { lost, received })
  // A counter going BACKWARDS means the connection was rebuilt underneath us
  // (reconnect); treat it as a fresh baseline instead of a negative delta.
  const deltaLost = previous && lost >= previous.lost ? lost - previous.lost : lost
  const deltaReceived = previous && received >= previous.received ? received - previous.received : received

  const rtt = rtts.length > 0 ? Math.max(...rtts) : null
  if (deltaLost + deltaReceived === 0 && rtt === null) return 'unknown'

  const lossRatio = deltaLost + deltaReceived > 0 ? deltaLost / (deltaLost + deltaReceived) : 0
  if (lossRatio < 0.02 && (rtt === null || rtt < 0.25)) return 'good'
  if (lossRatio < 0.08 && (rtt === null || rtt < 0.5)) return 'fair'
  return 'poor'
}
